import { useEffect, useState } from 'react';
import { Save, Phone, Mail, MapPin, Instagram, Facebook, Youtube, Twitter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleContactChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const handleSocialChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [field]: value }
    }));
  };

  const handlePaymentChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      payment: { ...prev.payment, [field]: value }
    }));
  };

  const handleSeoChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      seo: { ...prev.seo, [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-stone-900">Configuración</h1>
          <p className="text-stone-500 mt-1">Ajustes generales del sitio</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="settings-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      <Tabs defaultValue="contact" className="space-y-6">
        <TabsList className="bg-white border">
          <TabsTrigger value="contact">Contacto</TabsTrigger>
          <TabsTrigger value="social">Redes Sociales</TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="payment">Pagos</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Información de Contacto</CardTitle>
              <CardDescription>
                Datos de contacto que aparecerán en el sitio web
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  Número de WhatsApp
                </Label>
                <Input
                  value={settings?.contact?.whatsapp || ''}
                  onChange={(e) => handleContactChange('whatsapp', e.target.value)}
                  placeholder="+1 809 123 4567"
                  className="mt-2"
                  data-testid="settings-whatsapp"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Este número recibirá todas las reservaciones
                </p>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={settings?.contact?.email || ''}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  placeholder="info@caribbeangroovetours.com"
                  className="mt-2"
                  data-testid="settings-email"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  Ubicación / Dirección
                </Label>
                <Input
                  value={settings?.contact?.address || ''}
                  onChange={(e) => handleContactChange('address', e.target.value)}
                  placeholder="República Dominicana"
                  className="mt-2"
                  data-testid="settings-address"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Tab */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle>Redes Sociales</CardTitle>
              <CardDescription>
                Enlaces a tus perfiles de redes sociales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-500" />
                  Instagram
                </Label>
                <Input
                  value={settings?.social_links?.instagram || ''}
                  onChange={(e) => handleSocialChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="mt-2"
                  data-testid="settings-instagram"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook
                </Label>
                <Input
                  value={settings?.social_links?.facebook || ''}
                  onChange={(e) => handleSocialChange('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="mt-2"
                  data-testid="settings-facebook"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-red-600" />
                  YouTube
                </Label>
                <Input
                  value={settings?.social_links?.youtube || ''}
                  onChange={(e) => handleSocialChange('youtube', e.target.value)}
                  placeholder="https://youtube.com/..."
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-sky-500" />
                  Twitter / X
                </Label>
                <Input
                  value={settings?.social_links?.twitter || ''}
                  onChange={(e) => handleSocialChange('twitter', e.target.value)}
                  placeholder="https://twitter.com/..."
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp">
          <Card>
            <CardHeader>
              <CardTitle>Configuración de WhatsApp</CardTitle>
              <CardDescription>
                Personaliza el mensaje de reservación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Plantilla del Mensaje</Label>
                <Textarea
                  value={settings?.whatsapp_template || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_template: e.target.value }))}
                  placeholder="Mensaje de WhatsApp..."
                  className="mt-2 font-mono text-sm"
                  rows={5}
                  data-testid="settings-whatsapp-template"
                />
                <div className="mt-2 p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-600 font-medium mb-2">Variables disponibles:</p>
                  <div className="flex flex-wrap gap-2">
                    {['{tour}', '{date}', '{persons}', '{zone}', '{phone}', '{notes}'].map(v => (
                      <code key={v} className="px-2 py-1 bg-white rounded text-xs border">
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <Label>Moneda</Label>
                <Input
                  value={settings?.currency || 'USD'}
                  onChange={(e) => setSettings(prev => ({ ...prev, currency: e.target.value }))}
                  placeholder="USD"
                  className="mt-2 w-32"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Tab */}
        <TabsContent value="payment">
          <Card>
            <CardHeader>
              <CardTitle>Configuración de Pagos</CardTitle>
              <CardDescription>
                Configura las opciones de pago online
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between py-4 border-b">
                <div>
                  <Label className="text-base">Habilitar PayPal</Label>
                  <p className="text-sm text-stone-500">
                    Permite pagos con PayPal en los tours configurados
                  </p>
                </div>
                <Switch
                  checked={settings?.payment?.paypal_enabled || false}
                  onCheckedChange={(checked) => handlePaymentChange('paypal_enabled', checked)}
                  data-testid="settings-paypal-toggle"
                />
              </div>

              {settings?.payment?.paypal_enabled && (
                <>
                  <div>
                    <Label>PayPal Client ID</Label>
                    <Input
                      value={settings?.payment?.paypal_client_id || ''}
                      onChange={(e) => handlePaymentChange('paypal_client_id', e.target.value)}
                      placeholder="Tu PayPal Client ID"
                      className="mt-2"
                      data-testid="settings-paypal-client-id"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Obtén tu Client ID en{' '}
                      <a 
                        href="https://developer.paypal.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary underline"
                      >
                        developer.paypal.com
                      </a>
                    </p>
                  </div>

                  <div>
                    <Label>Modo</Label>
                    <select
                      value={settings?.payment?.paypal_mode || 'sandbox'}
                      onChange={(e) => handlePaymentChange('paypal_mode', e.target.value)}
                      className="mt-2 w-full h-10 px-3 rounded-md border border-stone-200"
                    >
                      <option value="sandbox">Sandbox (Pruebas)</option>
                      <option value="live">Live (Producción)</option>
                    </select>
                  </div>
                </>
              )}

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> También puedes configurar un link de pago 
                  individual en cada tour (PayPal.me, Stripe Link, etc.) desde la 
                  sección de edición del tour.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo">
          <Card>
            <CardHeader>
              <CardTitle>SEO y Metadatos</CardTitle>
              <CardDescription>
                Optimización para motores de búsqueda
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Título del Sitio</Label>
                <Input
                  value={settings?.seo?.title || ''}
                  onChange={(e) => handleSeoChange('title', e.target.value)}
                  placeholder="Caribbean Groove Tours - Excursiones en RD"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Meta Descripción</Label>
                <Textarea
                  value={settings?.seo?.description || ''}
                  onChange={(e) => handleSeoChange('description', e.target.value)}
                  placeholder="Descripción para Google y redes sociales..."
                  className="mt-2"
                  rows={3}
                />
                <p className="text-xs text-stone-500 mt-1">
                  Recomendado: 150-160 caracteres
                </p>
              </div>

              <div>
                <Label>Imagen OG (Open Graph)</Label>
                <Input
                  value={settings?.seo?.og_image || ''}
                  onChange={(e) => handleSeoChange('og_image', e.target.value)}
                  placeholder="URL de la imagen para compartir en redes"
                  className="mt-2"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
